'use client';

import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { Input } from '@/app/components/form/Input';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { CreateClientFormData, LoyaltyNumbersCategory } from '@/types/clientsApiTypes';
import type { Nullable } from '@/types/common';
import PlusIcon from '/public/icons/plus.svg';
import TrashIcon from '/public/icons/trash.svg';

const basePath = 'primary_passenger.loyalty_numbers';

export const EditLoyaltyProgramsForm = () => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: basePath,
  });

  const isAllowAddLoyaltyProgram = useMemo(() => {
    return fields.filter((field) => !field.is_legacy).every((field) => field.category_id || field.lp_id);
  }, [fields]);

  const handleAddLoyaltyProgram = () => {
    append(
      {
        lp_id: null,
        category_id: null,
        number: '',
        loyalty_program: '',
        frequent_flyer_number: '',
        is_legacy: false,
        note: '',
      },
      { shouldFocus: false }
    );
  };

  const mapOptions = useCallback(
    (options: LoyaltyNumbersCategory[]): SelectorOption<number>[] =>
      options.map((option) => ({
        value: option.id,
        label: option.title,
        frequent_flyer_number: option.frequent_flyer_number,
        note: '',
      })),
    []
  );

  const onLoyaltyProgramSelect = (option: SelectorOption<string | number> | null, fieldIndex: number) => {
    if (!option) return;
    const field = fields[fieldIndex];
    update(fieldIndex, {
      lp_id: field.lp_id,
      category_id: Number(option.value),
      number: field.number ?? '',
      loyalty_program: option.label,
      is_legacy: false,
      frequent_flyer_number: (option.frequent_flyer_number as Nullable<string>) ?? null,
      note: field.note ?? null,
    });
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      <div className="flex flex-col gap-3 w-full">
        {fields.map((field, fieldIndex) => (
          <div key={field.id} className="flex gap-4 items-end flex-wrap">
            {!field.category_id && field.loyalty_program ? (
              <div className="flex-1 min-w-[200px] max-md:min-w-full">
                <Input
                  className="w-full"
                  autoComplete="off"
                  value={field.loyalty_program ?? ''}
                  intent="primaryWhite"
                  label="Loyalty program"
                  name={`${basePath}.${fieldIndex}.loyalty_program`}
                  errors={errors}
                  disabled
                  readOnly
                />
              </div>
            ) : (
              <div className="flex-1 min-w-[200px] max-md:min-w-full">
                <InfinityAsyncSelector<LoyaltyNumbersCategory>
                  placeholder="Select"
                  url="/api/loyalty-numbers/categories"
                  queryKey={['loyalty-numbers-categories']}
                  mapOptions={mapOptions}
                  className="w-full"
                  isClearOption={false}
                  label="Loyalty program"
                  value={field.category_id ?? ''}
                  onChange={(option) => onLoyaltyProgramSelect(option, fieldIndex)}
                />
              </div>
            )}

            <div className="flex-1 min-w-[180px] max-md:min-w-full">
              <Input
                className="w-full"
                autoComplete="off"
                placeholder="Enter number"
                {...register(`${basePath}.${fieldIndex}.number`)}
                intent="primaryWhite"
                label="Number"
                errors={errors}
              />
            </div>
            <div className="contents max-md:flex max-md:w-full max-md:justify-end">
              <IconButton
                type="button"
                icon={<TrashIcon className="w-4 h-4" />}
                onClick={() => remove(fieldIndex)}
                color="danger"
              />
            </div>
          </div>
        ))}
      </div>

      <BaseButton
        onClick={handleAddLoyaltyProgram}
        type="button"
        size="ghost_small"
        startIcon={<PlusIcon />}
        intent="ghost"
        disabled={!isAllowAddLoyaltyProgram}
        className={clsx('w-fit', {
          'text-blue-300': isAllowAddLoyaltyProgram,
          'text-grey-700 cursor-not-allowed': !isAllowAddLoyaltyProgram,
        })}
      >
        Add New Loyalty Program
      </BaseButton>
    </div>
  );
};
